import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { LocalStorageService } from './local-storage.service';
import { AppAuthService } from '../cores/auth/app-auth-service';
import { ElectronService } from './electron.service';
import { getGoodsApiUrl } from '../shared/utils/api-url.util';

export interface PriceChangeEvent {
  eventType: string;
  skuCodes: string[];
}

@Injectable({
  providedIn: 'root',
})
export class PriceSseService implements OnDestroy {
  private eventSource: EventSourcePolyfill | null = null;
  private readonly priceEvents$ = new Subject<PriceChangeEvent>();
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 10;
  private readonly baseReconnectDelay = 1000;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private isConnecting = false;
  private shouldReconnect = true;
  private activeConsumers = 0;

  constructor(
    private readonly localStorageService: LocalStorageService,
    private readonly authService: AppAuthService,
    private readonly electronService: ElectronService,
  ) {}

  get priceEvents(): Observable<PriceChangeEvent> {
    return this.priceEvents$.asObservable();
  }

  connect(): void {
    this.activeConsumers++;
    void this.openConnection();
  }

  private async openConnection(): Promise<void> {
    if (this.eventSource || this.isConnecting) {
      return;
    }
    const token = this.localStorageService.getAccessToken();
    if (this.activeConsumers === 0) {
      this.isConnecting = false;
      return;
    }
    if (!token) {
      this.isConnecting = false;
      this.reconnectTimeout = setTimeout(() => void this.openConnection(), this.baseReconnectDelay);
      return;
    }

    this.shouldReconnect = true;
    this.isConnecting = true;
    const sseUrl = `${getGoodsApiUrl(this.electronService.isElectron)}/price-events/subscribe`;
    try {
      this.eventSource = new EventSourcePolyfill(sseUrl, {
        headers: {
          Authorization: this.authService.buildAuthHeader() || '',
        },
      });
      this.eventSource.onopen = () => {
        this.reconnectAttempts = 0;
        this.isConnecting = false;
      };
      this.eventSource.addEventListener('price-event', (event: Event) => {
        try {
          const messageEvent = event as MessageEvent<string>;
          const data = JSON.parse(messageEvent.data ?? '{}') as Partial<PriceChangeEvent>;
          const skuCodes = (data.skuCodes ?? []).filter((sku) => !!sku);
          if (!skuCodes.length) {
            return;
          }
          this.priceEvents$.next({
            eventType: data.eventType || 'price-changed',
            skuCodes,
          });
        } catch {
          return;
        }
      });
      this.eventSource.onerror = () => {
        this.isConnecting = false;
        this.handleConnectionError();
      };
    } catch {
      this.isConnecting = false;
      this.handleConnectionError();
    }
  }

  disconnect(): void {
    if (this.activeConsumers > 0) {
      this.activeConsumers--;
    }
    if (this.activeConsumers > 0) {
      return;
    }
    this.shouldReconnect = false;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.eventSource?.close();
    this.eventSource = null;
    this.isConnecting = false;
  }

  ngOnDestroy(): void {
    this.activeConsumers = 0;
    this.disconnect();
  }

  private handleConnectionError(): void {
    this.eventSource?.close();
    this.eventSource = null;
    if (!this.shouldReconnect || this.activeConsumers === 0) {
      return;
    }
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }
    const delay = this.baseReconnectDelay * 2 ** this.reconnectAttempts;
    this.reconnectAttempts++;
    this.reconnectTimeout = setTimeout(() => void this.openConnection(), delay);
  }
}
